import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { AtlassianTeamUpdateArchiveHydratedAction } from '../types';

import messages from './messages';

type ArchiveTeamsConfirmationModalProps = {
	actions: AtlassianTeamUpdateArchiveHydratedAction[];
	onClose: () => void;
	onConfirm: (action: MessageAction) => Promise<void>;
	onArchiveComplete?: () => void;
	onArchivingChange?: (teamIdsLoading: string[]) => void;
};

export const ArchiveTeamsConfirmationModal = ({
	actions,
	onClose,
	onConfirm,
	onArchiveComplete,
	onArchivingChange,
}: ArchiveTeamsConfirmationModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isArchiving, setIsArchiving] = useState(false);
	const { sendAnalyticsEvent } = useAnalytics();
	const teamCount = actions.length;

	const handleClose = useCallback(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'closeArchiveConfirmationModalCleanUpTeams',
			attributes: {
				teamCount,
			},
		});
		onClose();
	}, [onClose, sendAnalyticsEvent, teamCount]);

	const handleConfirm = useCallback(async () => {
		if (!actions.length) {
			onClose();
			return;
		}

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'confirmArchiveCleanUpTeams',
			attributes: {
				teamCount,
			},
		});

		setIsArchiving(true);
		onArchivingChange?.(actions.map((a) => a.data.teamId));

		await Promise.all(
			actions.map(async (action) => {
				await onConfirm(action);
			}),
		);
		onArchiveComplete?.();
		onArchivingChange?.([]);
		setIsArchiving(false);
		onClose();
	}, [
		actions,
		onConfirm,
		onArchiveComplete,
		onClose,
		sendAnalyticsEvent,
		teamCount,
		onArchivingChange,
	]);

	return (
		<ModalTransition>
			{actions.length > 0 && (
				<Modal width="small" onClose={handleClose}>
					<ModalHeader hasCloseButton>
						<ModalTitle appearance="warning">
							{formatMessage(messages.archiveModalTitle, { teamCount })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.150">
							<Text as="p">{formatMessage(messages.archiveModalBodyLine1, { teamCount })}</Text>
							<Text as="p">
								{formatMessage(messages.archiveModalBodyLine2, {
									teamCount,
									teamName: (
										<Text as="span" weight="bold">
											{actions[0]?.data.displayName}
										</Text>
									),
								})}
							</Text>
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Button appearance="subtle" onClick={handleClose} isDisabled={isArchiving}>
							{formatMessage(messages.archiveModalCancel)}
						</Button>
						<Button appearance="warning" onClick={handleConfirm} isLoading={isArchiving}>
							{formatMessage(messages.archiveModalConfirm, { teamCount })}
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
